import React, { FunctionComponent, useCallback, useMemo } from 'react'
import styles from './Gallery.module.sass'
import { FileData } from '../../../responses/files'
import { RootActions } from '../../../redux/root'
import { useDispatch } from 'react-redux'
import Image from '../../data-components/files/Image/Image'
import { Dispatch } from 'redux'
import { Dialog } from '../Dialog/Dialog'
import OkCancel from '../../controls/OkCancel/OkCancel'
import { usePrevious, useUpdateEffect } from 'react-use'
import clsx from 'clsx'
import { findIndex, lookup } from 'fp-ts/Array'
import { entityEqPredicate } from '../../../utils/entity-utils'
import * as O from 'fp-ts/Option'
import { useRootSelector } from '../../../utils/hooks'
import { pipe } from 'fp-ts/es6/function'
import { galleryActions } from '../../../redux/global/gallery/gallery-actions'
import { RequestStatus } from '../../../responses/response'
import TopBarProgress from 'react-topbar-progress-indicator'
import { useLocation } from 'react-router'
import GalleryMobile from './components/GalleryMobile/GalleryMobile'
import GalleryDesktop from './components/GalleryDesktop/GalleryDesktop'
import { AnimatePresence, motion, Variants } from 'framer-motion'
import { ActiveImageContext } from 'ts/components/data-components/files/Album/Album'
import { statusContext } from 'ts/constants/contexts'
import { useQueries } from '../../../utils/hooks/useQueries'
import { Colors } from '../../../styles'
import { GalleryEditState } from '../../../redux/global/gallery/gallery-reducer'
import { GalleryNavigation } from './navigation'

const variants: Variants = {
    closed: {opacity: 0, transform: 'scale(0)'},
    open: {opacity: 1, transform: 'scale(1)'},
}

const Gallery: FunctionComponent = () => {
    const dispatch = useDispatch<Dispatch<RootActions>>()
    const {open, current, data, editState, comments, status} = useRootSelector(state => state.global.gallery)
    const {pathname} = useLocation()

    const previous = usePrevious(current)

    const navigation = useMemo<GalleryNavigation>(() => {
        const files: FileData[] = comments.flatMap(comment => comment.files)
        const index = findIndex(entityEqPredicate(current))(files)
        return {
            prev: O.chain((index: number) => lookup(index - 1, files))(index),
            next: O.chain((index: number) => lookup(index + 1, files))(index),
            direction: pipe(
                O.Do,
                O.bind('index', () => index),
                O.bind('prevIndex', () => findIndex(entityEqPredicate(previous))(files)),
                O.chain(({index, prevIndex}) => O.some(index - prevIndex)),
                O.getOrElse(() => 0)
            )
        }
    }, [comments, current, previous])

    const {isMobile} = useQueries()

    useUpdateEffect(() => {
        dispatch(galleryActions.close())
    }, [dispatch, pathname])

    const onPrev = useCallback(() => {
        O.isSome(navigation.prev) && dispatch(galleryActions.setCurrent(navigation.prev.value))
    }, [dispatch, navigation.prev])

    const onNext = useCallback(() => {
        O.isSome(navigation.next) && dispatch(galleryActions.setCurrent(navigation.next.value))
    }, [dispatch, navigation.next])

    const onClose = useCallback(() => dispatch(galleryActions.close()), [dispatch])

    const onDelete = useCallback(() => {
        current && dispatch(galleryActions.startDelete(current))
    }, [dispatch, current])

    return <ActiveImageContext.Provider value={current}>
        <statusContext.Provider value={status}>
            {
                status === RequestStatus.FETCHING && <TopBarProgress/>
            }
            <AnimatePresence>
                {
                    open && <motion.div
                        className={clsx(styles.gallery, styles[data.type])}
                        variants={variants}
                        initial={'closed'}
                        animate={'open'}
                        exit={'closed'}
                    >
                        {
                            isMobile ? <GalleryMobile
                                current={current}
                                data={data}
                                navigation={navigation}
                                onPrev={onPrev}
                                onNext={onNext}
                                onClose={onClose}
                                onDelete={onDelete}
                            /> : <GalleryDesktop
                                current={current}
                                data={data}
                                navigation={navigation}
                                onPrev={onPrev}
                                onNext={onNext}
                                onClose={onClose}
                                onDelete={onDelete}
                            />
                        }
                    </motion.div>
                }
            </AnimatePresence>
            {
                 GalleryEditState.is.delete(editState) && <Dialog
                    header={'Подтвердите удаление файла'}
                    onClose={() => dispatch(galleryActions.cancelEdit())}
                    actions={({onClose}) => <OkCancel
                        color={Colors.red}
                        onOk={() => dispatch(galleryActions.delete.request())}
                        onCancel={onClose}
                    />}
                >
                    <Image className={styles.deletePreview} file={editState.file}/>
                </Dialog>
            }
        </statusContext.Provider>
    </ActiveImageContext.Provider>
}

export default Gallery